import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TimePicker } from "@/components/pickers";
import { formatTime12, shiftInfo } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Check, X } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending: ["Waiting for approval", "bg-amber-100 text-amber-900"],
  approved: ["Approved — shift is live", "bg-emerald-100 text-emerald-900"],
  rejected: ["Rejected", "bg-rose-100 text-rose-900"],
  withdrawn: ["Withdrawn", "bg-slate-100 text-slate-700"],
};
const EMPTY = { location_id: "", start: "", end: "", department_ids: [], reason: "" };

function useRequests(status) {
  const { actingOrgId } = useAuth();
  return useQuery({ queryKey: ["shift-requests", actingOrgId, status || "all"],
    queryFn: async () => (await api.get(`/roster/shift-requests${status ? `?status=${status}` : ""}`)).data });
}

const Summary = ({ r }) => (
  <p className="text-base text-slate-900">{r.code ? <><b>{r.code}</b> · {r.name} · </> : <b>New shift · </b>}{formatTime12(r.start)} – {formatTime12(r.end)}
    <span className="ml-2 text-sm text-slate-600">{shiftInfo(r.start, r.end)?.label} · {r.location_name}{r.department_names?.length ? ` · ${r.department_names.join(", ")}` : " · all departments"}</span></p>
);

export function ShiftRequestPanel() {
  const qc = useQueryClient();
  const { data = [] } = useRequests();
  const withdraw = async (id) => {
    try { await api.post(`/roster/shift-requests/${id}/withdraw`); qc.invalidateQueries({ queryKey: ["shift-requests"] }); toast.success("Request withdrawn"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <ShiftRequestForm onDone={() => qc.invalidateQueries({ queryKey: ["shift-requests"] })} />
      <Card className="p-5" data-testid="my-shift-requests">
        <p className="mb-3 text-base font-bold text-slate-900">My shift requests</p>
        <div className="space-y-2">
          {data.map((r) => {
            const [label, cls] = STATUS[r.status];
            return (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3" data-testid={`shift-request-${r.id}`}>
                <div><Summary r={r} />{r.review_comment && <p className="text-sm text-slate-700">Org admin: {r.review_comment}</p>}</div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-sm font-semibold ${cls}`} data-testid={`shift-request-status-${r.id}`}>{label}</span>
                  {r.status === "pending" && <Button size="sm" variant="outline" onClick={() => withdraw(r.id)} data-testid={`shift-request-withdraw-${r.id}`}>Withdraw</Button>}
                </div>
              </div>
            );
          })}
          {!data.length && <p className="text-sm text-slate-500">No requests yet.</p>}
        </div>
      </Card>
    </div>
  );
}

function ShiftRequestForm({ onDone }) {
  const { actingOrgId } = useAuth();
  const [f, setF] = useState(EMPTY);
  const { data: locs = [] } = useQuery({ queryKey: ["sr-locs", actingOrgId], queryFn: async () => (await api.get("/roster/shift-requests/locations")).data });
  const { data: depts = [] } = useQuery({ queryKey: ["r-depts", actingOrgId], queryFn: async () => (await api.get("/org/masters/departments")).data });
  const toggle = (id) => setF((p) => ({ ...p, department_ids: p.department_ids.includes(id) ? p.department_ids.filter((x) => x !== id) : [...p.department_ids, id] }));
  const submit = async () => {
    try { await api.post("/roster/shift-requests", f); setF(EMPTY); toast.success("Request sent to your org admin"); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const info = shiftInfo(f.start, f.end);
  return (
    <Card className="p-5" data-testid="shift-request-form">
      <p className="text-base font-bold text-slate-900">Request a new shift</p>
      <p className="mb-4 text-sm text-slate-600">Pick the location and timings you need. Your org admin names the shift, gives it a code and approves it; it then becomes available for rosters at that location.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56"><p className="mb-1 text-sm text-slate-700">Location</p>
          <Select value={f.location_id} onValueChange={(v) => setF({ ...f, location_id: v })}>
            <SelectTrigger data-testid="sr-location"><SelectValue placeholder="Select location" /></SelectTrigger>
            <SelectContent>{locs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><p className="mb-1 text-sm text-slate-700">Start</p><TimePicker value={f.start} onChange={(v) => setF({ ...f, start: v })} testid="sr-start" /></div>
        <div><p className="mb-1 text-sm text-slate-700">End</p><TimePicker value={f.end} onChange={(v) => setF({ ...f, end: v })} testid="sr-end" /></div>
        {info && <span className={`self-center rounded-md px-3 py-1.5 text-sm font-semibold ${info.overnight ? "bg-indigo-100 text-indigo-900" : "bg-slate-100 text-slate-800"}`} data-testid="sr-duration">{info.label}</span>}
      </div>
      <p className="mb-2 mt-4 text-sm font-semibold text-slate-800">Departments (leave empty for all)</p>
      <div className="flex flex-wrap gap-4">
        {depts.map((d) => <label key={d.id} className="flex items-center gap-2 text-sm"><Checkbox checked={f.department_ids.includes(d.id)} onCheckedChange={() => toggle(d.id)} data-testid={`sr-dept-${d.id}`} /> {d.name}</label>)}
      </div>
      <Input className="mt-4" placeholder="Why is this shift needed? (optional)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="sr-reason" />
      <Button className="mt-4" disabled={!f.location_id || !f.start || !f.end} onClick={submit} data-testid="sr-submit"><Send className="mr-1 h-4 w-4" /> Send request</Button>
    </Card>
  );
}

export function ShiftRequestApprovals({ onApproved }) {
  const qc = useQueryClient();
  const { data = [] } = useRequests("pending");
  if (!data.length) return null;
  return (
    <Card className="border-2 border-amber-300 bg-amber-50 p-5" data-testid="shift-request-approvals">
      <p className="mb-3 text-base font-bold text-amber-900">{data.length} shift request(s) from center managers</p>
      <div className="space-y-2">{data.map((r) => <ApprovalRow key={r.id} r={r} onDone={(kind) => { qc.invalidateQueries({ queryKey: ["shift-requests"] }); if (kind === "approve") onApproved?.(); }} />)}</div>
    </Card>
  );
}

function ApprovalRow({ r, onDone }) {
  const [comment, setComment] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (kind) => {
    setBusy(true);
    try { await api.post(`/roster/shift-requests/${r.id}/${kind}`, { comment, code, name }); toast.success(kind === "approve" ? `Shift ${code.toUpperCase()} added` : "Request rejected"); onDone(kind); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg bg-white px-4 py-3" data-testid={`sr-approval-${r.id}`}>
      <Summary r={r} />
      <p className="text-sm text-slate-600">Requested by {r.requested_by}{r.reason ? ` — “${r.reason}”` : ""}</p>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div><p className="mb-1 text-xs text-slate-600">Shift code</p><Input maxLength={4} placeholder="e.g. N" className="w-24 uppercase placeholder:normal-case placeholder:text-slate-400" value={code} onChange={(e) => setCode(e.target.value)} data-testid={`sr-code-${r.id}`} /></div>
        <div><p className="mb-1 text-xs text-slate-600">Shift name</p><Input placeholder="e.g. Night" className="w-40 placeholder:text-slate-400" value={name} onChange={(e) => setName(e.target.value)} data-testid={`sr-name-${r.id}`} /></div>
        <Input className="max-w-sm" placeholder="Comment (required to reject)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid={`sr-comment-${r.id}`} />
        <Button size="sm" disabled={busy || !code.trim() || !name.trim()} onClick={() => act("approve")} data-testid={`sr-approve-${r.id}`}><Check className="mr-1 h-4 w-4" /> Approve</Button>
        <Button size="sm" variant="outline" disabled={busy} className="text-rose-700" onClick={() => act("reject")} data-testid={`sr-reject-${r.id}`}><X className="mr-1 h-4 w-4" /> Reject</Button>
      </div>
    </div>
  );
}
